import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Edit3, Save, RotateCcw, X, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import "../../../styles/Header-css/ServicePartner/PartnerProfile/ScheduleManager.css";

// Helper function to generate schedule (can be outside the component)
const generateWeekSchedule = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const schedule = [];
    for (let i = 0; i < 7; i++) {
        const date = new Date();
        date.setDate(date.getDate() + i);
        const isSunday = date.getDay() === 0;
        const timeSlots = [
            { time: '08:00', available: !isSunday, booked: false },
            { time: '09:00', available: !isSunday, booked: i === 1, bookedBy: i === 1 ? 'Priya S.' : undefined },
            { time: '10:00', available: !isSunday, booked: false },
            { time: '11:00', available: !isSunday, booked: false },
            { time: '12:00', available: false, booked: false },
            { time: '13:00', available: !isSunday, booked: false },
            { time: '14:00', available: !isSunday, booked: i === 2, bookedBy: i === 2 ? 'Rahul K.' : undefined },
            { time: '15:00', available: !isSunday, booked: false },
            { time: '16:00', available: !isSunday, booked: false },
            { time: '17:00', available: !isSunday, booked: false },
            { time: '18:00', available: false, booked: false },
        ];
        schedule.push({
            date: date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }),
            day: days[date.getDay()],
            isActive: !isSunday,
            timeSlots,
        });
    }
    return schedule;
};


export function ScheduleManager() {
    const [isEditMode, setIsEditMode] = useState(false);
    const [weekSchedule, setWeekSchedule] = useState([]);
    const [originalSchedule, setOriginalSchedule] = useState([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedRecurringDays, setSelectedRecurringDays] = useState([]);

    useEffect(() => {
        setWeekSchedule(generateWeekSchedule());
    }, []);

    const handleEditClick = () => {
        setOriginalSchedule(JSON.parse(JSON.stringify(weekSchedule)));
        setIsEditMode(true);
    };

    // ✅ FIXED: The call to onScheduleUpdate has been removed.
    const handleSaveClick = () => {
        setIsEditMode(false);
        // In a real app, you might save `weekSchedule` to localStorage or an API here
        console.log("Schedule Saved:", weekSchedule);
        toast.success('Schedule updated successfully!');
    };

    const handleCancelClick = () => {
        setWeekSchedule(originalSchedule);
        setIsEditMode(false);
    };
    
    // All other handler functions remain the same
    const toggleTimeSlot = (dayIndex, slotIndex) => {
        const slot = weekSchedule[dayIndex].timeSlots[slotIndex];
        if (slot.booked) {
            toast.error('Cannot modify booked time slots');
            return;
        }
        const newSchedule = JSON.parse(JSON.stringify(weekSchedule));
        newSchedule[dayIndex].timeSlots[slotIndex].available = !slot.available;
        setWeekSchedule(newSchedule);
    };

    const toggleDay = (dayIndex) => {
        const newSchedule = JSON.parse(JSON.stringify(weekSchedule));
        const day = newSchedule[dayIndex];
        day.isActive = !day.isActive;
        day.timeSlots.forEach(slot => {
            if (!slot.booked) {
                slot.available = day.isActive;
            }
        });
        setWeekSchedule(newSchedule);
    };

    const handleRecurringDayChange = (day, isChecked) => {
        if (isChecked) {
            setSelectedRecurringDays(prev => [...prev, day]);
        } else {
            setSelectedRecurringDays(prev => prev.filter(d => d !== day));
        }
    };
    
    const applyRecurringPattern = () => {
        if (selectedRecurringDays.length === 0) {
            toast.error('Please select at least one day');
            return;
        }
        const patternSource = weekSchedule[0].timeSlots;
        const newSchedule = JSON.parse(JSON.stringify(weekSchedule));
        newSchedule.forEach(day => {
            if (selectedRecurringDays.includes(day.day)) {
                day.isActive = true;
                day.timeSlots.forEach((slot, index) => {
                    if (!slot.booked) {
                        slot.available = patternSource[index].available;
                    }
                });
            }
        });
        setWeekSchedule(newSchedule);
        toast.success('Recurring pattern applied!');
        setIsDialogOpen(false);
    };
    
    const getSlotColorClass = (slot) => {
        if (slot.booked) return 'booked';
        if (slot.available) return 'available';
        return 'unavailable';
    };
    
    return (
        <div className="schedule-page-container">
            <Link to="/partner-profile" className="back-to-dashboard">
                <ArrowLeft size={16} /> Back to Dashboard
            </Link>
            <main className="container">
                <div className="card">
                    <div className="card-header">
                        <h2 className="card-title">
                            <Calendar className="icon" /> My Schedule
                        </h2>
                        <div className="controls">
                            {isEditMode ? (
                                <div className="edit-controls" style={{display: 'flex'}}>
                                    <button onClick={handleCancelClick} className="btn btn-outline">Cancel</button>
                                    <button onClick={handleSaveClick} className="btn">
                                        <Save className="icon-sm" /> Save
                                    </button>
                                </div>
                            ) : (
                                <div className="view-controls" style={{display: 'flex'}}>
                                    <button onClick={handleEditClick} className="btn">
                                        <Edit3 className="icon-sm" /> Edit
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card-content">
                        {isEditMode && (
                            <div className="quick-actions">
                                <button onClick={() => setIsDialogOpen(true)} className="btn btn-outline">
                                    <RotateCcw className="icon-sm" /> Set Recurring
                                </button>
                            </div>
                        )}
                        
                        <div className="schedule-container">
                            {weekSchedule.map((day, dayIndex) => (
                                <div key={day.date} className="day-card">
                                    <div className="day-header">
                                        <div className="day-info">
                                            <div className={`day-date ${day.isActive ? 'active' : ''}`}>
                                                <p className="day-name">{day.day}</p>
                                                <p className="date-str">{day.date}</p>
                                            </div>
                                            {isEditMode && (
                                                <label className="switch">
                                                    <input type="checkbox" checked={day.isActive} onChange={() => toggleDay(dayIndex)} />
                                                    <span className="slider"></span>
                                                </label>
                                            )}
                                        </div>
                                        <div className="day-summary">
                                            <p className="summary-available">{day.timeSlots.filter(s => s.available && !s.booked).length} available</p>
                                            <p className="summary-booked">{day.timeSlots.filter(s => s.booked).length} booked</p>
                                        </div>
                                    </div>
                                    <div className="time-slots-grid">
                                        {day.timeSlots.map((slot, slotIndex) => (
                                            <button
                                                key={slot.time}
                                                className={`time-slot ${getSlotColorClass(slot)} ${isEditMode && !slot.booked ? 'editable' : ''}`}
                                                onClick={() => toggleTimeSlot(dayIndex, slotIndex)}
                                                disabled={!isEditMode || slot.booked}
                                            >
                                                <div>{slot.time}</div>
                                                {slot.booked && slot.bookedBy && <div className="booked-by">{slot.bookedBy}</div>}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="legend">
                            <div className="legend-item"><span className="legend-color available"></span>Available</div>
                            <div className="legend-item"><span className="legend-color booked"></span>Booked</div>
                            <div className="legend-item"><span className="legend-color unavailable"></span>Unavailable</div>
                        </div>
                    </div>
                </div>

                {isDialogOpen && (
                    <div className="dialog-overlay open" onClick={() => setIsDialogOpen(false)}>
                        <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
                            <div className="dialog-header">
                                <h3 className="dialog-title">Set Recurring Schedule</h3>
                                <button onClick={() => setIsDialogOpen(false)} className="dialog-close"><X size={20} /></button>
                            </div>
                            <p className="dialog-description">Select days to apply the first day's availability pattern:</p>
                            <div className="dialog-grid">
                                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                                    <label key={day} className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            checked={selectedRecurringDays.includes(day)}
                                            onChange={(e) => handleRecurringDayChange(day, e.target.checked)}
                                        />
                                        <span>{day}</span>
                                    </label>
                                ))}
                            </div>
                            <button onClick={applyRecurringPattern} className="btn btn-full">Apply Pattern</button>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}